import { z } from "zod";
import {
  buildSocketExpressionsFromEdges,
  type WorkflowExpressionEdge,
  type WorkflowExpressionStep,
} from "./expressions";

/** The step-config key the editor stores admin-authored expressions under. */
export const MANUAL_EXPRESSION_OVERRIDES_CONFIG_KEY =
  "__manual_expression_overrides";

const manualExpressionOverridesSchema = z.record(
  z.string(),
  z.union([z.string(), z.null()]),
);

/**
 * Admin-authored expressions set via the socket right-click menu, keyed by
 * socket id, read from `config.__manual_expression_overrides`.
 *
 * A value of `null` means "explicitly cleared this session" — distinct from
 * an absent key, which means "this session never touched this socket's
 * override" (so a save must not claim to know it should be cleared). Both the
 * editor's `graphToDefinition` and the backend's `mapStepInputToModel` rely on
 * this three-way distinction: only an explicit clear removes a persisted
 * override; an untouched socket preserves whatever the server already has.
 */
export function readManualExpressionOverrides(
  config: Record<string, unknown> | undefined,
): Record<string, string | null> {
  const raw = config?.[MANUAL_EXPRESSION_OVERRIDES_CONFIG_KEY];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const parsed = manualExpressionOverridesSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

export interface ManualOverrideGraphSocket {
  id: string;
  /** Defaults to "any" when omitted. */
  type?: string;
}

export interface ManualOverrideGraphStep {
  id: string;
  inputSockets: ManualOverrideGraphSocket[];
  outputSockets: ManualOverrideGraphSocket[];
  /**
   * Manual overrides by input socket id, in the
   * {@link readManualExpressionOverrides} shape: a string is an active
   * override, `null` or an absent key falls back to the edge-derived
   * expression.
   */
  overrides: Readonly<Record<string, string | null>>;
}

/** An unsaved expression for one input socket, validated in place. */
export interface ManualOverrideCandidate {
  stepId: string;
  socketId: string;
  expression: string;
}

function currentExpression(
  step: ManualOverrideGraphStep,
  socketId: string,
  edgeExpressions: ReadonlyMap<string, string>,
  candidate: ManualOverrideCandidate | undefined,
): string | undefined {
  if (candidate?.stepId === step.id && candidate.socketId === socketId) {
    return candidate.expression;
  }
  const override = step.overrides[socketId];
  if (typeof override === "string") return override;
  return edgeExpressions.get(`${step.id}:${socketId}`);
}

/**
 * Reconstruct every step's real current input expression — manual override if
 * set, otherwise edge-derived, exactly like the editor save path — with one
 * socket's expression optionally replaced by a not-yet-saved candidate.
 *
 * Used to validate a socket-expression edit (syntax, references, type
 * compatibility, cycles) against the graph's true current state before saving
 * it. Passing an empty `inputSockets: []` for every step but the one being
 * edited — the naive approach — would hide a cycle formed by TWO reciprocal
 * overrides, since checking one edit at a time would only ever see its own
 * side of the cycle.
 *
 * Edges missing a socket id on either end are ignored.
 */
export function buildManualOverrideValidationSteps(
  steps: readonly ManualOverrideGraphStep[],
  edges: readonly WorkflowExpressionEdge[],
  candidate?: ManualOverrideCandidate,
): WorkflowExpressionStep[] {
  const edgeExpressions = buildSocketExpressionsFromEdges(
    edges.filter((edge) => edge.sourceSocketId && edge.targetSocketId),
  );

  return steps.map((step) => ({
    id: step.id,
    inputSockets: step.inputSockets.map((socket) => ({
      id: socket.id,
      type: socket.type || "any",
      expression: currentExpression(
        step,
        socket.id,
        edgeExpressions,
        candidate,
      ),
    })),
    outputSockets: step.outputSockets.map((socket) => ({
      id: socket.id,
      type: socket.type || "any",
    })),
  }));
}
